// @flow
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Modal,
  Table,
  TableBody,
  TableHead,
} from '@fortress-technology-solutions/fortress-component-library/Molecules';
import useIntl from '../../../../../hooks/useIntl';
import generalMessages from '../../../../App/messages';
import type { CountsByStatus } from '../types';
import messages from './messages';

type Props = {
  open: boolean,
  count: number,
  countsByStatus: CountsByStatus,
  onContinue: () => void,
  onCancel: () => void,
};

const TABLE_SX = {
  '& .MuiTableCell-root': { px: 1, py: 1, borderBottom: 'none' },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: 'primary.main',
    color: 'common.white',
  },
  '& .MuiTableBody-root .MuiTableRow-root': {
    backgroundColor: 'background.paper',
  },
  '& .MuiTableBody-root .MuiTableRow-root:last-of-type': {
    backgroundColor: 'primary.lighter',
    color: 'primary.main',
  },
};

const COUNT_CELL_SX = { textAlign: 'right', width: 110, pr: 1.5 };

const STATUS_ROWS = [
  { key: 'Waitlist', message: messages.statusWaiting },
  { key: 'In Process', message: messages.statusInProcess },
  { key: 'Canceled', message: messages.statusCanceled },
  { key: 'Denied', message: messages.statusDenied },
];

function EnableWaitlistModal({
  open,
  count,
  countsByStatus,
  onContinue,
  onCancel,
}: Props) {
  const intl = useIntl();
  const hasApplications = count > 0;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      status="info"
      icon={<AlertInfoIcon />}
      title={intl.formatMessage(messages.enableTitle)}
      actions={
        <>
          <Button variant="text" onClick={onCancel}>
            <FormattedMessage {...generalMessages.cancel} />
          </Button>
          <Button variant="primary" onClick={onContinue}>
            <FormattedMessage {...messages.continue} />
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        {hasApplications ? (
          <>
            <Typography variant="bodyMedium">
              <FormattedMessage {...messages.enableIntro} />
            </Typography>
            <Typography variant="bodyMedium">
              <FormattedMessage {...messages.enableTableIntro} />
            </Typography>
            <Table size="small" sx={TABLE_SX}>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="labelLarge" color="inherit">
                      <FormattedMessage {...messages.statusHeader} />
                    </Typography>
                  </TableCell>
                  <TableCell sx={COUNT_CELL_SX}>
                    <Typography variant="labelLarge" color="inherit">
                      <FormattedMessage {...messages.countHeader} />
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {STATUS_ROWS.map(({ key, message }) => (
                  <TableRow key={key}>
                    <TableCell>
                      <Typography variant="bodySmall">
                        <FormattedMessage {...message} />
                      </Typography>
                    </TableCell>
                    <TableCell sx={COUNT_CELL_SX}>
                      <Typography variant="bodySmall">
                        {countsByStatus[key]}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell>
                    <Typography variant="labelMedium" color="inherit">
                      <FormattedMessage {...messages.total} />
                    </Typography>
                  </TableCell>
                  <TableCell sx={COUNT_CELL_SX}>
                    <Typography variant="labelMedium" color="inherit">
                      {count}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography variant="bodyMedium">
              <FormattedMessage {...messages.enableFooter} />
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="bodyMedium">
              <FormattedMessage {...messages.enableNoneEligible} />
            </Typography>
            <Typography variant="bodyMedium">
              <FormattedMessage {...messages.enableNoneFooter} />
            </Typography>
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default EnableWaitlistModal;
